import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { getLang } from "app/feature/constants";
import { Skeleton } from "@material-ui/lab";
import InputTextField from "components/input/inputTextField";
import SearchableSelect from "modules/survey/components/select/searchableSelect";
import moment from "moment";
import { StarRounded as StarIcon } from "@material-ui/icons";
import { surveyQuestionType } from "modules/survey/constants";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    borderRadius: 16,
    minHeight: 143,
    display: "flex",
    justifyContent: "space-between",
    scrollbarWidth: "thin",
    backgroundColor: "#ffffff",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 13,
  },
  value: {
    fontSize: 14,
    color: "#202020 !important",
  },
  root: {
    margin: "7px 10px 7px 9px",
    padding: 0,
    color: "#202020 !important",
  },
  button: {
    textWrap: "nowrap",
    textAlign: "center",
    borderRadius: 100,
    padding: "6px 16px",
    minWidth: "11em",
    color: "white",
    fontWeight: "bold",
    textTransform: "capitalize",
  },
}));

export default function SurveyDetailCardComponent({
  survey,
  lang,
  isLoading,
  uiSetting,
}) {
  const classes = useStyles();

  if (
    survey &&
    (moment() > moment(survey.endDate) || survey.status === "unpublished")
  ) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <Box p={2.5} style={{ width: "100%" }}>
        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            style={{ width: "100%" }}
          >
            <Skeleton width={"100%"} height={30} />
            <Skeleton width={"100%"} height={30} />
            <Skeleton width={"100%"} height={30} />
            <Skeleton width={"100%"} height={30} />
          </Box>
        ) : survey ? (
          <Box>
            <Box mb={1}>
              <Typography
                className={classes.title}
                style={{ color: uiSetting.colorPrimary }}
              >
                {survey?.name}
              </Typography>
            </Box>
            <Divider />
            <Box mb={3} mt={1.5}>
              <Typography className={classes.subtitle}>
                {getLang(lang, "label.AVAILABLE_UNTIL")}:{" "}
                {survey?.endDate
                  ? moment(survey?.endDate).format("DD MMM YYYY")
                  : "-"}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2" color="textPrimary" style={{ whiteSpace: "pre-wrap" }}>
                {survey?.description}
              </Typography>
            </Box>
            <Box style={{ flex: 1 }}>
              {survey?.questions?.map((item, i) => (
                <Box
                  style={{ display: "flex", margin: "25px 0px" }}
                  key={item + i}
                >
                  <Typography className={classes.title} color="textPrimary">
                    {i + 1}.
                  </Typography>
                  <Box marginLeft={1} style={{ width: "100%" }}>
                    <Typography
                      className={classes.title}
                      style={{ marginBottom: 8 }}
                      color="textPrimary"
                    >
                      {item.text}
                      {item.required && (
                        <span
                          style={{
                            color: "#FD646F",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {" "}
                          *
                        </span>
                      )}
                    </Typography>
                    {item.type === surveyQuestionType.text && (
                      <InputTextField
                        fullWidth
                        disabled={true}
                        placeholder={item.placeholder}
                        style={{
                          fontWeight: 500,
                        }}
                        InputStyle={{
                          fontSize: 14,
                        }}
                      />
                    )}
                    {item.type === surveyQuestionType.selection && (
                      <>
                        {item.answers.map((choice, i) => (
                          <FormControlLabel
                            key={i}
                            control={
                              <Radio
                                className={classes.root}
                                size="small"
                                disableRipple
                                disabled={true}
                                color="primary"
                              />
                            }
                            label={item.isV2 ? choice.title : choice}
                            classes={{
                              label: classes.value,
                            }}
                            style={{
                              display: "flex",
                            }}
                          />
                        ))}
                      </>
                    )}
                    {item.type === surveyQuestionType.dropdownlist && (
                      <>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: "600",
                            color: "rgba(32, 32, 32, 0.33)",
                            fontSize: "14px",
                          }}
                        ></Typography>
                        <>
                          <SearchableSelect
                            searchable={true}
                            placeholder={getLang(
                              lang,
                              "placeholder.SELECT_AN_OPTION"
                            )}
                            dropdownItem={item.answers}
                            disabled={true}
                          />
                        </>
                      </>
                    )}
                    {item.type ===
                      surveyQuestionType.multipleChoiceMultiSelect && (
                      <>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: "600",
                            color: "rgba(32, 32, 32, 0.33)",
                            fontSize: "14px",
                          }}
                        ></Typography>
                        <>
                          {item.answers.map((choice, checkBoxIndex) => (
                            <FormControlLabel
                              key={checkBoxIndex}
                              classes={{
                                label: classes.value,
                              }}
                              control={
                                <Checkbox
                                  disabled={true}
                                  className={classes.root}
                                  value={choice}
                                  color="primary"
                                />
                              }
                              label={item.isV2 ? choice.title : choice}
                            />
                          ))}
                        </>
                      </>
                    )}
                    {item.type === surveyQuestionType.multiStarRating && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: 16 }}
                        mt={1}
                      >
                        {item.answers.map((choice, index) => (
                          <Box key={index} display="flex">
                            <Box mr={1.5}>●</Box>
                            <Box>
                              <Typography variant="body2">
                                {choice.title}
                              </Typography>
                              <Box
                                style={{ gap: 4 }}
                                display="flex"
                                alignItems="center"
                                mt={1}
                              >
                                {[...new Array(5)].map((_, starIndex) => (
                                  <StarIcon
                                    key={starIndex}
                                    color="primary"
                                    style={{
                                      color: "#AAAAAA",
                                      fontSize: 28,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {item.type === surveyQuestionType.starRating && (
                      <Box mt={1.5}>
                        <Typography
                          variant="body2"
                          style={{ color: "#A8A8A8" }}
                          align="center"
                        >
                          {getLang(lang, "label.NOT_RATED")}
                        </Typography>
                        <Box
                          style={{ gap: 4 }}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mt={0.5}
                        >
                          {[...new Array(5)].map((_, starIndex) => (
                            <StarIcon
                              key={starIndex}
                              color="primary"
                              style={{
                                color: "#AAAAAA",
                                fontSize: 28,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Divider />
            <Box
              mt={1.75}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "0px 15px",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.button}
                disableElevation={true}
                style={{
                  backgroundColor: uiSetting.colorPrimary,
                }}
              >
                {getLang(lang, "label.SUBMIT")}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
